import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";
import { Editor } from "primereact/editor";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";

import systemStore from "../../storage/SystemStore";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import adminStore from "../../storage/AdminStore";

import Input from "../../ui/Input";
import Btn from "../../ui/Btn";
import CheckBox from "../../ui/CheckBox";
import PageLayout from "../PageLayout";

import { SAFE_FORMATS } from "../../common/utils";
import { AGREEMENT_CLUB1_OWNER } from "../../models/Agreement";

import "../../common/game.css";

const emptyTranslation = () => ({
  title: "",
  message: "",
});

const normalizeTranslation = (value) => {
  if (!value || typeof value !== "object") {
    return emptyTranslation();
  }

  return {
    title: value.title || "",
    message: value.message || value.text || "",
  };
};

const hasHtmlText = (value) => {
  return (value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;
};

const normalizeLanguage = (language, fallbackCode = null) => {
  if (typeof language === "string") {
    return {
      code: language,
      label: language.toUpperCase(),
    };
  }

  const code =
    language?.code ||
    language?.id ||
    language?.value ||
    language?.key ||
    fallbackCode;

  if (!code) return null;

  return {
    code,
    label:
      language?.name ||
      language?.name_native ||
      language?.title ||
      language?.label ||
      String(code).toUpperCase(),
  };
};

const getLanguageOptions = (languages) => {
  if (Array.isArray(languages)) {
    return languages
      .map((language) => normalizeLanguage(language))
      .filter(Boolean);
  }

  if (languages && typeof languages === "object") {
    return Object.entries(languages)
      .map(([code, language]) => {
        if (typeof language === "string") {
          return {
            code,
            label: language,
          };
        }

        return normalizeLanguage(language, code);
      })
      .filter(Boolean);
  }

  return [];
};

const EditAgreement = ({basePath}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const store = basePath === "/admin" ? adminStore : clubStore;
  const agreement = store.agreement;

  const [showAddTranslation, setShowAddTranslation] = useState(false);
  const [newLanguage, setNewLanguage] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!id) return;

    if (id === "0") {
      store.initAgreement();
    } else {
      store.getAgreement(id);
    }
  }, [id]);

  if (!agreement) return null;

  const data = agreement.data && typeof agreement.data === "object"
    ? agreement.data
    : {};

  const translations = data.translations && typeof data.translations === "object"
    ? data.translations
    : {};

  const languageOptions = getLanguageOptions(appStore.languages);
  const addedLanguages = new Set(Object.keys(translations));
  const availableLanguages = languageOptions.filter(
    (language) => !addedLanguages.has(language.code)
  );

  const getLanguageLabel = (code) => {
    return languageOptions.find((language) => language.code === code)?.label
      || String(code).toUpperCase();
  };

  const setTranslations = (nextTranslations) => {
    agreement.setProperty("data", {
      ...data,
      translations: nextTranslations,
    });
  };

  const addTranslation = () => {
    if (!newLanguage || translations[newLanguage]) return;

    setTranslations({
      ...translations,
      [newLanguage]: emptyTranslation(),
    });

    setNewLanguage(null);
    setShowAddTranslation(false);
  };

  const updateTranslation = (language, prop, value) => {
    const current = normalizeTranslation(translations[language]);

    setTranslations({
      ...translations,
      [language]: {
        ...current,
        [prop]: value,
      },
    });
  };

  const removeTranslation = (language) => {
    const nextTranslations = {...translations};
    delete nextTranslations[language];
    setTranslations(nextTranslations);
  };


  const openPreview = (title, message, language = null) => {
    setPreview({
      title: title || appStore.tr.preview || "Preview",
      message: message || "",
      language,
    });
  };

  const translationsAreValid = Object.values(translations).every((value) => {
    const translation = normalizeTranslation(value);
    return !!translation.title.trim() && hasHtmlText(translation.message);
  });

  const handleSave = () => {
    agreement
      .saveAgreement()
      .then(() => {
        systemStore.setSuccess(appStore.tr.successfully_saved);
        navigate(`${basePath}/agreements`);
      })
      .catch((error) => {
        systemStore.setError(
          appStore.tr[error?.message] || error?.message || "Error"
        );
      });
  };

  return (
    <PageLayout
      header={
        <div className="col-12">
          <h3 className="m-0">
            {agreement.type === AGREEMENT_CLUB1_OWNER
              ? appStore.t("owner_agreement")
              : appStore.t("member_agreement")}
          </h3>
        </div>
      }
    >
      <div className="flex flex-column">
        {/* Main title */}
        <div className="col-12">
          <label>{appStore.tr.name}</label>
          <Input store={agreement} prop="title" />
        </div>

        {/* Version */}
        <div className="col-12">
          <label>{appStore.tr.version}</label>
          <Input store={agreement} prop="version" />
        </div>

        {/* Active */}
        <div className="col-12">
          <label>{appStore.tr.is_active}</label>
          <CheckBox store={agreement} prop="is_active" />
        </div>

        {/* Main text */}
        <div className="col-12">
          <label>{appStore.tr.text}</label>
          <Editor
            value={agreement.text || ""}
            onTextChange={(e) =>
              agreement.setProperty("text", e.htmlValue || "")
            }
            style={{ height: "320px" }}
            formats={SAFE_FORMATS}
          />

          <div className="mt-2">
            <Btn
              icon="pi pi-eye"
              severity="secondary"
              label={appStore.tr.preview || "Preview"}
              onClick={() => openPreview(agreement.title, agreement.text)}
              disabled={!hasHtmlText(agreement.text)}
            />
          </div>
        </div>

        {/* Translations */}
        <div className="col-12 mt-3">
          <div className="flex justify-content-between align-items-center mb-2">
            <strong>{appStore.tr.translations || "Переводы"}</strong>

            {availableLanguages.length > 0 && !showAddTranslation ? (
              <Btn
                icon="pi pi-plus"
                severity="info"
                label={appStore.tr.add_translation || "Добавить перевод"}
                onClick={() => setShowAddTranslation(true)}
              />
            ) : null}
          </div>

          {showAddTranslation ? (
            <div className="game-card mb-3">
              <div className="flex flex-column md:flex-row gap-2 align-items-end">
                <div className="flex-1">
                  <label>{appStore.tr.language || "Язык"}</label>
                  <Dropdown
                    value={newLanguage}
                    options={availableLanguages}
                    optionLabel="label"
                    optionValue="code"
                    onChange={(e) => setNewLanguage(e.value)}
                    placeholder={appStore.tr.select_language || "Выберите язык"}
                    className="w-full"
                  />
                </div>

                <div className="flex gap-2">
                  <Btn
                    icon="pi pi-plus"
                    severity="success"
                    label={appStore.tr.add || "Добавить"}
                    onClick={addTranslation}
                    disabled={!newLanguage}
                  />
                  <Btn
                    icon="pi pi-times"
                    severity="secondary"
                    label={appStore.tr.cancel || "Отмена"}
                    onClick={() => {
                      setNewLanguage(null);
                      setShowAddTranslation(false);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {Object.entries(translations).map(([language, rawTranslation]) => {
            const translation = normalizeTranslation(rawTranslation);

            return (
              <div key={language} className="game-card mb-3">
                <div className="flex justify-content-between align-items-center mb-3">
                  <strong>
                    {getLanguageLabel(language)} ({String(language).toUpperCase()})
                  </strong>

                  <Btn
                    icon="pi pi-trash"
                    severity="danger"
                    label={appStore.tr.delete || "Удалить"}
                    onClick={() => removeTranslation(language)}
                  />
                </div>

                <div className="mb-3">
                  <label>{appStore.tr.name}</label>
                  <Input
                    store={{
                      title: translation.title,
                      setProperty: (prop, value) =>
                        updateTranslation(language, "title", value),
                    }}
                    prop="title"
                  />
                </div>

                <div>
                  <label>{appStore.tr.text}</label>
                  <Editor
                    value={translation.message}
                    onTextChange={(e) =>
                      updateTranslation(language, "message", e.htmlValue || "")
                    }
                    style={{ height: "260px" }}
                    formats={SAFE_FORMATS}
                  />

                  <div className="mt-2">
                    <Btn
                      icon="pi pi-eye"
                      severity="secondary"
                      label={appStore.tr.preview || "Preview"}
                      onClick={() =>
                        openPreview(
                          translation.title,
                          translation.message,
                          getLanguageLabel(language)
                        )
                      }
                      disabled={!hasHtmlText(translation.message)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Save */}
        <div className="col-12">
          <Btn
            icon="pi pi-save"
            severity="success"
            label={appStore.tr.save}
            onClick={handleSave}
            disabled={
              !agreement.title ||
              !hasHtmlText(agreement.text) ||
              !agreement.type ||
              !translationsAreValid
            }
          />
        </div>
      </div>

      <Dialog
        visible={!!preview}
        modal
        header={
          preview?.language
            ? `${preview.language}: ${preview.title}`
            : preview?.title
        }
        onHide={() => setPreview(null)}
        style={{ width: "min(900px, 95vw)" }}
        contentStyle={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        <div
          className="agreement-preview"
          dangerouslySetInnerHTML={{ __html: preview?.message || "" }}
        />
      </Dialog>
    </PageLayout>
  );
};

export default observer(EditAgreement);